import axios from "axios";
import { useEffect, useState } from "react";
import {
  Linkedin,
  Sparkles,
  Send,
  RefreshCw,
  Loader2,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  FileText,
  X,
  MessageSquare,
  Search,
  UserCheck,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

// Maya interest classification badge styles (iter67). Mirror of
// LinkedInInboxPage so the badge looks identical across the app.
const INTEREST_TAB_STYLES = {
  hot:   { label: "Quente", emoji: "🔥", cls: "bg-rose-100 text-rose-700 border-rose-200" },
  warm:  { label: "Morno",  emoji: "🟢", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cold:  { label: "Frio",   emoji: "❄️", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  noise: { label: "Ruído",  emoji: "🚫", cls: "bg-slate-50 text-slate-400 border-slate-200" },
};

/**
 * LinkedIn actions tab for a candidate already in the funnel.
 * Lives inside CandidateModal.
 *
 * Features:
 *   - Show LinkedIn profile URL + CV status
 *   - "Refazer CV do LinkedIn" — rebuild PDF using the latest profile data
 *   - "Enviar InMail com Maya" — same flow as the Sourcing page but pre-filled
 *     with this candidate + their job context
 */
const CandidateLinkedInTab = ({ candidate, job, onUpdated }) => {
  const profileUrl = candidate.linkedin_profile_url || "";
  const cvStatus = candidate.cv_status || ""; // "" | pending | building | ready | failed
  const cvError = candidate.cv_error || "";

  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [accountsLoading, setAccountsLoading] = useState(true);

  const [rebuilding, setRebuilding] = useState(false);

  // InMail composer
  const [inmailOpen, setInmailOpen] = useState(false);
  const [extraInstructions, setExtraInstructions] = useState("");
  const [generating, setGenerating] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [draftSource, setDraftSource] = useState(""); // "whatsapp_template" | "maya_ai"
  const [providerId, setProviderId] = useState("");
  const [sending, setSending] = useState(false);

  // History of LinkedIn messages with this candidate
  const [historyThreads, setHistoryThreads] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  // Manual LinkedIn URL linking (when candidate came via another channel)
  const [linkInput, setLinkInput] = useState("");
  const [linking, setLinking] = useState(false);

  // Auto-find this candidate on LinkedIn (iter70)
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null); // null = not searched
  const [searchedQuery, setSearchedQuery] = useState("");

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(
        `${API}/candidates/${candidate.id}/linkedin-history`,
      );
      setHistoryThreads(res.data.threads || []);
    } catch {
      // Best-effort: don't break the tab if history fetch fails
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate.id]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/integrations/synkly/accounts`);
        const connected = (res.data || []).filter(
          (a) => a.channel === "linkedin" && (a.status === "connected" || a.status === "active"),
        );
        setAccounts(connected);
        if (connected.length >= 1) setSelectedAccount(connected[0].synkly_account_id);
      } finally {
        setAccountsLoading(false);
      }
    };
    load();
  }, []);

  const handleRebuild = async () => {
    if (!profileUrl) {
      toast.error("Candidato sem URL do LinkedIn cadastrada.");
      return;
    }
    setRebuilding(true);
    try {
      const res = await axios.post(
        `${API}/candidates/${candidate.id}/rebuild-linkedin-cv`,
      );
      toast.success(res.data.message || "Reprocessamento iniciado.");
      // Best-effort: ask parent to refresh in ~30s
      setTimeout(() => onUpdated?.(), 30000);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Falha ao reprocessar CV.",
      );
    } finally {
      setRebuilding(false);
    }
  };

  const handleOpenInmail = () => {
    if (!profileUrl) {
      toast.error("Candidato sem URL do LinkedIn cadastrada.");
      return;
    }
    if (!selectedAccount) {
      toast.error("Conecte uma conta LinkedIn em Integrações → LinkedIn primeiro.");
      return;
    }
    setSubject("");
    setBody("");
    setDraftSource("");
    setExtraInstructions("");
    setInmailOpen(true);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/integrations/linkedin/messages/draft-inmail`,
        {
          linkedin_account_id: selectedAccount,
          job_id: job?.id || candidate.job_id,
          profile_url: profileUrl,
          extra_instructions: extraInstructions.trim(),
          // Pass the candidate's name as a hint so backend can:
          //   1. Render the welcome template with the right name even when
          //      Unipile returns sparse profile data (private/restricted)
          //   2. Fall back to a name-based LinkedIn search to resolve the
          //      provider_id when /users/{id} returns nothing
          candidate_name_hint: candidate.name || "",
        },
      );
      setSubject(res.data.subject || "");
      setBody(res.data.body || "");
      setDraftSource(res.data.source || "");
      setProviderId(res.data.provider_id || "");
      toast.success(
        res.data.source === "whatsapp_welcome_template"
          ? "InMail montado com o template de boas-vindas do WhatsApp."
          : res.data.source === "job_whatsapp_template"
          ? "InMail montado com o template de WhatsApp da vaga."
          : "Maya gerou o InMail. Revise antes de enviar.",
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Falha ao gerar InMail.",
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject e mensagem são obrigatórios.");
      return;
    }
    if (!providerId) {
      toast.error("Gere o InMail primeiro pra resolver o LinkedIn ID.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/integrations/linkedin/messages/send`, {
        linkedin_account_id: selectedAccount,
        attendee_provider_id: providerId,
        text: body,
        subject,
        inmail: true,
        job_id: job?.id || candidate.job_id,
        candidate_id: candidate.id,
      });
      toast.success(`InMail enviado para ${candidate.name}`);
      setInmailOpen(false);
      // Refresh history so the just-sent message appears immediately
      loadHistory();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Falha ao enviar InMail.",
      );
    } finally {
      setSending(false);
    }
  };

  // ─── Empty state (no LinkedIn URL) ──────────────────────────────
  // Lets the recruiter PASTE a LinkedIn URL to "ativar" the LinkedIn flow
  // for candidates that entered via other channels (manual upload, public
  // form, WhatsApp). Once saved, the parent re-renders with the full tab
  // (InMail, CV builder, message history, etc.) — same code path as a
  // LinkedIn-sourced candidate.
  const handleLinkLinkedIn = async (e) => {
    e?.preventDefault?.();
    const url = (linkInput || "").trim();
    if (!url) {
      toast.error("Cole o link do perfil do LinkedIn.");
      return;
    }
    setLinking(true);
    try {
      const res = await axios.patch(`${API}/candidates/${candidate.id}/contact`, {
        linkedin_profile_url: url,
      });
      toast.success("LinkedIn vinculado! As ações agora estão disponíveis.");
      setLinkInput("");
      // Pass the updated candidate doc back to the parent so the modal
      // re-renders with the new linkedin_profile_url. Calling onUpdated()
      // without args crashes the parent's `updated.id` lookup → white screen.
      onUpdated?.(res.data);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Falha ao vincular o LinkedIn.",
      );
    } finally {
      setLinking(false);
    }
  };

  // Auto-find candidate on LinkedIn: searches by name, biased by job location
  const handleFindOnLinkedIn = async () => {
    setSearching(true);
    setSearchResults(null);
    try {
      const res = await axios.post(
        `${API}/candidates/${candidate.id}/find-linkedin`,
      );
      setSearchResults(res.data.profiles || []);
      setSearchedQuery(res.data.searched_keywords || candidate.name || "");
      if ((res.data.profiles || []).length === 0) {
        toast.info("Nenhum perfil encontrado. Tente colar a URL manualmente.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Falha ao buscar no LinkedIn.",
      );
    } finally {
      setSearching(false);
    }
  };

  const handlePickProfile = async (profile) => {
    setLinking(true);
    try {
      const res = await axios.patch(`${API}/candidates/${candidate.id}/contact`, {
        linkedin_profile_url: profile.profile_url,
      });
      toast.success(
        `Vinculado a ${profile.name || "perfil selecionado"}.`,
      );
      onUpdated?.(res.data);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Falha ao vincular este perfil.",
      );
    } finally {
      setLinking(false);
    }
  };

  if (!profileUrl) {
    // Pre-loaded suggestions from the background auto-link worker (iter71).
    // These are stamped on the candidate doc by `linkedin_auto_link.py` right
    // after CV analysis completes. When present, we surface them BEFORE the
    // manual paste form, so the recruiter can confirm with 1 click.
    const preloadedSuggestions = candidate.linkedin_suggestions || [];
    const autoStatus = candidate.linkedin_auto_link_status || "";
    const hasPreloaded = preloadedSuggestions.length > 0 && autoStatus === "suggestions";
    // Display = preloaded suggestions (auto-discovery) OR explicit search results
    const displayResults = searchResults !== null ? searchResults : (hasPreloaded ? preloadedSuggestions : null);
    const displayQuery = searchResults !== null ? searchedQuery : (hasPreloaded ? candidate.name : "");

    return (
      <div
        data-testid="candidate-linkedin-tab"
        className="bg-blue-50/50 border border-blue-100 rounded-lg p-6"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="bg-white rounded-lg p-2 shadow-sm">
            <Linkedin className="w-5 h-5 text-brand-blue" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-slate-900 mb-1">
              Vincular perfil do LinkedIn
            </h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Este candidato não veio via LinkedIn, mas você pode colar a URL do
              perfil dele aqui pra ativar <strong>InMail</strong>, montagem de
              CV via LinkedIn, histórico de mensagens e classificação Maya — as
              mesmas ações disponíveis pra prospects.
            </p>
          </div>
        </div>

        {/* Auto-discovery hint banner (iter71) — shown when the background
            worker found suggestions but couldn't auto-link with confidence */}
        {hasPreloaded && searchResults === null && (
          <div
            data-testid="auto-link-suggestion-banner"
            className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-2"
          >
            <Sparkles className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900 leading-relaxed">
              <strong>Sugerimos LinkedIn:</strong> a Maya encontrou{" "}
              {preloadedSuggestions.length} perfil(is) que podem ser este(a)
              candidato(a) — escolha o correto abaixo ou pule e use a busca manual.
            </div>
          </div>
        )}
        <form onSubmit={handleLinkLinkedIn} className="flex flex-col sm:flex-row gap-2">
          <Input
            data-testid="link-linkedin-input"
            type="url"
            placeholder="https://www.linkedin.com/in/nome-do-candidato"
            value={linkInput}
            onChange={(e) => setLinkInput(e.target.value)}
            disabled={linking}
            className="flex-1 bg-white"
          />
          <Button
            type="submit"
            data-testid="link-linkedin-submit"
            disabled={linking || !linkInput.trim()}
            className="bg-brand-blue hover:bg-brand-blue/90"
          >
            {linking ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Vinculando…
              </>
            ) : (
              <>
                <Linkedin className="w-4 h-4 mr-2" />
                Vincular LinkedIn
              </>
            )}
          </Button>
        </form>

        {/* Divider with OR */}
        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
            ou
          </span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        {/* Auto-find on LinkedIn */}
        <Button
          type="button"
          variant="outline"
          data-testid="find-linkedin-btn"
          onClick={handleFindOnLinkedIn}
          disabled={searching || linking}
          className="w-full border-brand-blue/30 text-brand-blue hover:bg-brand-blue/5"
        >
          {searching ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Buscando no LinkedIn…
            </>
          ) : (
            <>
              <Search className="w-4 h-4 mr-2" />
              Buscar candidato no LinkedIn pelo nome
            </>
          )}
        </Button>

        {/* Search results — inline list of matches (from auto-discovery OR manual search) */}
        {displayResults !== null && (
          <div
            data-testid="find-linkedin-results"
            className="mt-4 bg-white border border-slate-200 rounded-lg overflow-hidden"
          >
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold text-slate-600">
              {displayResults.length > 0 ? (
                <>
                  {displayResults.length} perfil(is) encontrado(s)
                  {displayQuery && (
                    <> buscando por <span className="text-slate-900">"{displayQuery}"</span></>
                  )}
                  {" "}— escolha o correto:
                </>
              ) : (
                <>Nenhum resultado para "{displayQuery}". Cole a URL manualmente acima.</>
              )}
            </div>
            <ul className="divide-y divide-slate-100">
              {displayResults.map((p, idx) => (
                <li
                  key={p.provider_id || p.profile_url || idx}
                  data-testid={`find-linkedin-result-${idx}`}
                  className="p-3 flex items-center gap-3 hover:bg-slate-50 transition-colors"
                >
                  {p.picture_url ? (
                    <img
                      src={p.picture_url}
                      alt={p.name}
                      className="w-10 h-10 rounded-full object-cover border border-slate-200 flex-shrink-0"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Linkedin className="w-4 h-4 text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-semibold text-slate-900 truncate">
                        {p.name || "Sem nome"}
                      </span>
                      {/* Match-score badge from the auto-link worker */}
                      {typeof p.match_score === "number" && p.match_score > 0 && (
                        <span
                          className={`text-[9px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded ${
                            p.match_score >= 70 ? "bg-emerald-100 text-emerald-700" :
                            p.match_score >= 50 ? "bg-amber-100 text-amber-700" :
                            "bg-slate-100 text-slate-500"
                          }`}
                          title={`Confiança Maya: ${p.match_score}%`}
                        >
                          {p.match_score}%
                        </span>
                      )}
                      <a
                        href={p.profile_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-400 hover:text-brand-blue"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    {p.headline && (
                      <div className="text-xs text-slate-600 truncate">
                        {p.headline}
                      </div>
                    )}
                    {p.location && (
                      <div className="text-[10px] text-slate-400 truncate">
                        {p.location}
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    data-testid={`find-linkedin-pick-${idx}`}
                    onClick={() => handlePickProfile(p)}
                    disabled={linking}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white flex-shrink-0"
                  >
                    {linking ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <>
                        <UserCheck className="w-3 h-3 mr-1" />
                        Este
                      </>
                    )}
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="text-[10px] text-slate-400 mt-3">
          Dica: você encontra a URL no LinkedIn → Perfil do candidato → ⋯ →
          Copiar link do perfil.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="candidate-linkedin-tab" className="space-y-5">
      {/* Profile summary card */}
      <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <Linkedin className="w-4 h-4 text-brand-blue" />
          <h3 className="text-sm font-semibold text-slate-900">
            Perfil do LinkedIn
          </h3>
        </div>
        <a
          href={profileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-mono text-brand-blue hover:underline break-all inline-flex items-center gap-1"
        >
          {profileUrl}
          <ExternalLink className="w-3 h-3 shrink-0" />
        </a>

        {/* CV status badge */}
        {cvStatus && (
          <div className="mt-3 flex items-center gap-2">
            {cvStatus === "ready" && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">
                <CheckCircle2 className="w-3 h-3" />
                CV gerado e analisado pela IA
              </span>
            )}
            {(cvStatus === "pending" || cvStatus === "building") && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
                <Loader2 className="w-3 h-3 animate-spin" />
                Gerando CV…
              </span>
            )}
            {cvStatus === "failed" && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded">
                <AlertCircle className="w-3 h-3" />
                Falha ao gerar CV
              </span>
            )}
          </div>
        )}
        {cvStatus === "failed" && cvError && (
          <p className="text-[11px] text-rose-600 mt-1.5">{cvError}</p>
        )}
      </div>

      {/* Actions grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-slate-700" />
            <h4 className="text-sm font-semibold text-slate-900">
              Refazer CV do LinkedIn
            </h4>
          </div>
          <p className="text-xs text-slate-600 leading-relaxed mb-3">
            Re-busca o perfil completo no LinkedIn via Synkly, gera um novo PDF com
            experiência, formação, skills, idiomas e certificações, e roda a IA
            de novo para atualizar o score contra a vaga.
          </p>
          <Button
            data-testid="linkedin-tab-rebuild-cv"
            size="sm"
            variant="outline"
            onClick={handleRebuild}
            disabled={rebuilding || accountsLoading}
            className="w-full"
          >
            {rebuilding ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            )}
            Reprocessar CV agora
          </Button>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-violet-600" />
            <h4 className="text-sm font-semibold text-slate-900">
              Enviar InMail com Maya
            </h4>
          </div>
          <p className="text-xs text-slate-600 leading-relaxed mb-3">
            Maya monta uma mensagem personalizada (ou usa o template de
            WhatsApp da vaga, se houver). Você revisa e envia direto pelo
            LinkedIn.
          </p>
          <Button
            data-testid="linkedin-tab-inmail"
            size="sm"
            onClick={handleOpenInmail}
            disabled={accountsLoading || accounts.length === 0}
            className="w-full bg-violet-600 hover:bg-violet-700 text-white"
          >
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            Compor InMail
          </Button>
          {accounts.length === 0 && !accountsLoading && (
            <p className="text-[10px] text-amber-700 mt-2">
              Nenhuma conta LinkedIn conectada.{" "}
              <a href="/integracoes/linkedin" className="underline">
                Conecte uma
              </a>
              .
            </p>
          )}
        </div>
      </div>

      {/* Conversation history with this candidate */}
      {historyThreads.length > 0 && (
        <div
          data-testid="linkedin-tab-history"
          className="bg-white border border-slate-200 rounded-lg p-5"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <MessageSquare className="w-4 h-4 text-slate-700" />
              <h4 className="text-sm font-semibold text-slate-900">
                Histórico no LinkedIn
              </h4>
              <span className="text-[10px] font-medium text-slate-500">
                {historyThreads.reduce((acc, t) => acc + (t.messages?.length || 0), 0)}{" "}
                mensagens
              </span>
              {historyThreads.map((t) =>
                INTEREST_TAB_STYLES[t.interest_level] ? (
                  <span
                    key={`int-${t.chat_id}`}
                    data-testid={`tab-interest-${t.interest_level}`}
                    title={t.interest_reason || ""}
                    className={`text-[9px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded border ${INTEREST_TAB_STYLES[t.interest_level].cls}`}
                  >
                    {INTEREST_TAB_STYLES[t.interest_level].emoji}{" "}
                    {INTEREST_TAB_STYLES[t.interest_level].label}
                  </span>
                ) : null,
              )}
            </div>
            <a
              href="/comunicacoes/linkedin"
              className="text-[11px] font-medium text-brand-blue hover:underline inline-flex items-center gap-1"
            >
              Abrir caixa completa
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          {historyThreads
            .filter((t) => t.interest_reason)
            .slice(0, 1)
            .map((t) => (
              <p
                key={`reason-${t.chat_id}`}
                className="text-[11px] text-slate-500 -mt-2 mb-2 italic"
              >
                Sinal Maya: {t.interest_reason}
              </p>
            ))}

          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
            {historyThreads.map((t) =>
              (t.messages || []).map((m) => (
                <div
                  key={m.id}
                  className={`flex ${
                    m.direction === "outbound" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                      m.direction === "outbound"
                        ? "bg-brand-blue text-white"
                        : "bg-slate-100 text-slate-900"
                    }`}
                  >
                    {m.subject && (
                      <div
                        className={`text-[10px] font-bold uppercase mb-1 pb-1 border-b ${
                          m.direction === "outbound"
                            ? "text-blue-100 border-blue-300"
                            : "text-slate-500 border-slate-300"
                        }`}
                      >
                        {m.subject}
                      </div>
                    )}
                    <div className="whitespace-pre-wrap leading-relaxed">{m.text}</div>
                    <div
                      className={`text-[10px] mt-1 flex items-center gap-1 ${
                        m.direction === "outbound" ? "text-blue-100" : "text-slate-500"
                      }`}
                    >
                      {m.is_from_maya && (
                        <>
                          <Sparkles className="w-2.5 h-2.5" />
                          <span>Maya · </span>
                        </>
                      )}
                      <span>
                        {new Date(m.created_at).toLocaleString("pt-BR", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              )),
            )}
          </div>
        </div>
      )}

      {historyLoading && (
        <div className="text-center text-xs text-slate-500 py-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" />
        </div>
      )}

      {/* InMail composer — inline (avoids nested Dialog crash inside CandidateModal) */}
      {inmailOpen && (
        <div
          data-testid="linkedin-tab-inmail-composer"
          className="bg-white border-2 border-violet-200 rounded-lg p-5 shadow-sm"
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h4 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-violet-600" />
                InMail para {candidate.name}
              </h4>
              <p className="text-xs text-slate-600 mt-0.5">
                Maya gera com base no perfil do LinkedIn + na vaga "
                {job?.title || "—"}".
              </p>
            </div>
            <button
              type="button"
              onClick={() => setInmailOpen(false)}
              disabled={sending}
              aria-label="Fechar"
              className="text-slate-400 hover:text-slate-700 shrink-0 p-1 rounded hover:bg-slate-100"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-700 mb-1.5 block">
                Instruções extras para Maya (opcional)
              </label>
              <Input
                data-testid="linkedin-tab-inmail-extra"
                value={extraInstructions}
                onChange={(e) => setExtraInstructions(e.target.value)}
                placeholder='ex: "Mencionar que aceita PJ ou CLT"'
              />
            </div>
            <Button
              data-testid="linkedin-tab-inmail-generate"
              onClick={handleGenerate}
              disabled={generating}
              variant="outline"
              className="w-full text-violet-700 border-violet-200 hover:bg-violet-50"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              {subject ? "Gerar novamente" : "Gerar com Maya"}
            </Button>

            {subject && (
              <>
                {draftSource && (
                  <div
                    className={`text-[11px] rounded-md px-2.5 py-1.5 border ${
                      draftSource === "whatsapp_template"
                        ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                        : "bg-violet-50 border-violet-200 text-violet-800"
                    }`}
                  >
                    {draftSource === "whatsapp_template" ? (
                      <>
                        ✅ <strong>Usando seu template de WhatsApp desta vaga</strong> — mesma mensagem nos 2 canais.
                      </>
                    ) : (
                      <>
                        ✨ <strong>Gerado pela Maya com IA</strong> — configure um template de WhatsApp na vaga para uniformizar.
                      </>
                    )}
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium text-slate-700 mb-1.5 block">
                    Assunto
                  </label>
                  <Input
                    data-testid="linkedin-tab-inmail-subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    maxLength={200}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700 mb-1.5 block">
                    Mensagem
                  </label>
                  <Textarea
                    data-testid="linkedin-tab-inmail-body"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={8}
                    className="text-sm"
                  />
                  <div className="text-[10px] text-slate-500 mt-1">
                    {body.length} caracteres
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                  <Button
                    variant="ghost"
                    onClick={() => setInmailOpen(false)}
                    disabled={sending}
                  >
                    Cancelar
                  </Button>
                  <Button
                    data-testid="linkedin-tab-inmail-send"
                    onClick={handleSend}
                    disabled={!subject || !body || sending}
                    className="bg-brand-blue hover:bg-brand-blue-dark text-white"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Enviar InMail
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateLinkedInTab;
